import React from 'react';
import PropTypes from 'prop-types';
import { ANCHOR } from 'react-google-maps/lib/constants';
import deepEqual from 'deep-equal';

import 'snazzy-info-window/dist/snazzy-info-window.min.css';

const open = (infoWindow) => {
    setTimeout(() => {
        if (!infoWindow._destroyed) {
            infoWindow.open();
        }
    });
};

const close = (infoWindow) => {
    setTimeout(() => {
        if (!infoWindow._destroyed) {
            infoWindow.close();
        }
    });
};

let Snazzy = null;

class SnazzyInfoWindow extends React.Component {
    // State parameters to retrieve from the parent component
    static contextTypes = {
        [ANCHOR]: PropTypes.object,
    };

    // Recreate the info window when these properties change.
    static immutableProperties = [
        'maxHeight',
        'maxWidth',
        'placement',
        'border',
        'pointer',
        'shadow',
        'closeOnMapClick',
        'openOnMarkerClick',
        'closeWhenOthersOpen',
    ];

    constructor(props, context) {
        super(props, context);
        this.state = {
            infoWindow: this.createInfoWindow(props, context),
        };
    }

    // Snazzy Info Window has a dependency on the global google variable.
    // Since we are using an async google maps loader we need to
    // only load the Snazzy Info Window plugin when it is needed.
    createInfoWindow = (props, context) => {
        const that = this;
        if (Snazzy === null) {
            Snazzy = require('snazzy-info-window');
        }
        return new Snazzy({
            marker: context[ANCHOR],
            ...props.options,
            callbacks: {
                afterOpen() {
                    // When the info window opens we need to update the preview location
                    props.onLocationChange();
                    // Always pan on open after the first time
                    that.panOnOpen = true;
                },
                beforeOpen() {
                    // Update the pan on open value
                    this._opts.panOnOpen = that.panOnOpen;
                },
            },
        });
    };

    // Deep equality check for properties which can be objects
    shouldRecreate = (nextProps) => {
        // Handle for empty options
        const options = this.props.options || {};
        const nextOptions = nextProps.options || {};
        const immutableChanged = SnazzyInfoWindow.immutableProperties.some(
            (p) => !deepEqual(options[p], nextOptions[p])
        );
        const missingKey = Object.keys(options).some((k) => options[k] !== undefined && nextOptions[k] === undefined);
        return immutableChanged || missingKey;
    };

    resetOpenState = (infoWindow, props, mapReset) => {
        const {
            behavior: { openByDefault },
            isEditMode,
            isActive,
        } = props;

        if (isEditMode) {
            // In edit mode open the active info window
            this.panOnOpen = !mapReset;
            if (isActive) {
                open(infoWindow);
            } else {
                close(infoWindow);
            }
        } else {
            // In normal mode perform open by default
            this.panOnOpen = false;
            if (openByDefault) {
                open(infoWindow);
            } else {
                close(infoWindow);
            }
        }
    };

    componentDidMount() {
        this.resetOpenState(this.state.infoWindow, this.props, false);
    }

    componentWillUnmount() {
        this.state.infoWindow.destroy();
    }

    UNSAFE_componentWillReceiveProps(nextProps, nextContext) {
        if (this.shouldRecreate(nextProps)) {
            this.setState({
                infoWindow: this.createInfoWindow(nextProps, nextContext),
            });
        }
    }

    UNSAFE_componentWillUpdate(nextProps, nextState) {
        const nextInfo = nextState.infoWindow;
        const oldInfo = this.state.infoWindow;
        const oldProps = this.props;

        if (oldInfo !== nextInfo) {
            open(nextInfo);
        }

        const enterEditMode = !oldProps.isEditMode && nextProps.isEditMode;
        const exitEditMode = oldProps.isEditMode && !nextProps.isEditMode;
        const resetMap = !oldProps.isMapReset && nextProps.isMapReset;

        if (enterEditMode || exitEditMode || resetMap) {
            this.resetOpenState(nextInfo, nextProps, resetMap);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const i = this.state.infoWindow;
        if (prevState.infoWindow === i) {
            i._opts = {
                ...i._opts,
                ...this.props.options,
            };
            if (this.props.options) {
                i.setContent(this.props.options.content);
            }
            i.draw();
            i.resize();
            i.reposition();
        } else {
            prevState.infoWindow.destroy();
            prevState.infoWindow._destroyed = true;
        }
    }

    render() {
        return false;
    }
}

export default SnazzyInfoWindow;
